import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { ifcRootGuid } from '@constants'
import produce from 'immer'
import { findIndex } from 'lodash-es'
import type { GlbFileAdditionalData } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIfcStore } from '@stores'
import { useControlStore } from '@editorStores'
import { useIfcFiles } from '@editorHooks'
import { getProjectFiles } from '@queries'
import { postFileMetadata } from '@mutations'
import { getAlignmentAngleDegFromThreePoints } from '../components/IFCAlignmentPanel/misc'

interface ApplyIfcAlignment {
  /** Save the z-rotation derived from the three picked points, then clear the points. */
  applyAlignment: () => void
  /** Reset the saved alignment back to the identity transformation and clear the points. */
  resetAlignment: () => void
  /** Whether all three points are set (i.e. `applyAlignment` will do something). */
  canApply: boolean
}

/**
 * Shared apply/reset logic for the scene-picked IFC alignment. Used by the alignment panel's
 * buttons and by the alignment scene HUD so both trigger the same file-metadata mutation.
 */
const useApplyIfcAlignment = (): ApplyIfcAlignment => {
  const { projectId } = useParams()
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const setApplyIfcAlignmentConfig = useControlStore(state => state.setApplyIfcAlignmentConfig)

  const glbFiles = useIfcFiles()
  const activeFile = useMemo(
    () =>
      activeIfcFileGuid
        ? glbFiles.find((f: FileItem) => f.guid === activeIfcFileGuid) ?? null
        : null,
    [activeIfcFileGuid, glbFiles],
  )

  const firstPoint = useIfcStore(state => state.firstPoint)
  const secondPoint = useIfcStore(state => state.secondPoint)
  const thirdPoint = useIfcStore(state => state.thirdPoint)
  const resetPoints = useIfcStore(state => state.resetPoints)

  const queryClient = useQueryClient()
  const { mutate: saveAlignmentConfig } = useMutation({
    mutationFn: async (payload: {
      alignment_config: GlbFileAdditionalData['alignment_config']
    }) => {
      if (!projectId || !activeFile) {
        throw new Error('Cannot save IFC alignment: no active project or IFC file')
      }
      return postFileMetadata.request(projectId, ifcRootGuid, {
        item_guid: activeFile.guid,
        name: activeFile.name ?? '',
        ...(activeFile.additional_data || {}),
        alignment_config: payload.alignment_config,
      })
    },
    onSuccess: (data: FileItem) => {
      const queryKey = getProjectFiles.getKey(projectId!, ifcRootGuid)
      queryClient.setQueryData<FilesResult>(
        queryKey,
        produce<FilesResult>(draft => {
          if (!draft) return
          const index = findIndex(draft.files, { guid: data.guid })
          if (index !== -1) draft.files[index].additional_data = data.additional_data
        }),
      )
    },
    mutationKey: ['saveAlignmentConfig', projectId, ifcRootGuid, activeIfcFileGuid],
    meta: {
      title: 'Anpassen der IFC-Ausrichtung',
      description: 'Die Ausrichtung des IFC-Modells in der Scene',
    },
  })

  const canApply = !!(firstPoint && secondPoint && thirdPoint)

  const applyAlignment = useCallback(() => {
    if (!firstPoint || !secondPoint || !thirdPoint) return
    const angleDeg = getAlignmentAngleDegFromThreePoints(firstPoint, secondPoint, thirdPoint)
    saveAlignmentConfig({
      alignment_config: {
        referencePointIFCCS: [secondPoint.x, secondPoint.y, secondPoint.z],
        referencePointModelCS: [0, 0, 0],
        zRotationDegrees: angleDeg,
      },
    })
    resetPoints()
    // Re-enable the applied alignment so the freshly saved transform is shown on the model.
    setApplyIfcAlignmentConfig(true)
  }, [
    firstPoint,
    secondPoint,
    thirdPoint,
    saveAlignmentConfig,
    resetPoints,
    setApplyIfcAlignmentConfig,
  ])

  const resetAlignment = useCallback(() => {
    resetPoints()
    saveAlignmentConfig({
      alignment_config: {
        referencePointIFCCS: [0, 0, 0],
        referencePointModelCS: [0, 0, 0],
        zRotationDegrees: 0,
      },
    })
  }, [resetPoints, saveAlignmentConfig])

  return { applyAlignment, resetAlignment, canApply }
}

export default useApplyIfcAlignment
